import { useState, useEffect, useCallback } from 'react';
import type {
  GeoJSONFeatureCollection,
  FilterState,
  MapBounds,
  ParcelDetail,
} from '../types/filters';
import { fetchParcelsGeoJSON, fetchParcelDetails } from '../services/api';

interface UseParcelsResult {
  parcels: GeoJSONFeatureCollection | null;
  selectedParcel: ParcelDetail | null;
  loading: boolean;
  error: string | null;
  loadParcels: (bounds: MapBounds) => void;
  selectParcel: (id: string | null) => void;
}

export function useParcels(filters: FilterState): UseParcelsResult {
  const [parcels, setParcels] = useState<GeoJSONFeatureCollection | null>(null);
  const [selectedParcel, setSelectedParcel] = useState<ParcelDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bounds, setBounds] = useState<MapBounds | null>(null);

  const loadParcels = useCallback((newBounds: MapBounds) => {
    setBounds(newBounds);
  }, []);

  useEffect(() => {
    if (!bounds) return;

    const currentBounds = bounds; // Capture for closure
    const controller = new AbortController();
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchParcelsGeoJSON(currentBounds, filters);
        if (!cancelled) {
          setParcels(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load parcels');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [bounds, filters]);

  const selectParcel = useCallback(async (id: string | null) => {
    if (!id) {
      setSelectedParcel(null);
      return;
    }

    try {
      const detail = await fetchParcelDetails(id);
      setSelectedParcel(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load parcel details');
    }
  }, []);

  return {
    parcels,
    selectedParcel,
    loading,
    error,
    loadParcels,
    selectParcel,
  };
}
